import { useEffect, useMemo } from 'react'

import { usePermissions } from 'commons/customHooks/useAuth'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { getUser } from 'commons/utils/user'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { OriginOrder, TypesValidations } from 'constants/enums'
import { URL_CONST } from 'constants/urls'
import TopSection from 'container/CreateOrder/components/TopSection'
import { getPostOnBody } from 'container/ManageOrders/utils'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as DraftOV from 'services/draft'
import { clearMaterialSelected } from 'store/ProductContext'
import { setCheckedRecords, setDuplicateOrder } from 'store/SalesOrderContext/salesOrderSlice'
import { setConfirmedNavigation } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'

import type { ClientsListInterface } from '../ModalPostOn'

interface ConfigOrderProp {
  step: number
  isButtonRescueData?: boolean
  hasOVs?: boolean
}

function ConfigOrder({ step, isButtonRescueData = false, hasOVs = false }: ConfigOrderProp) {
  const { draftBody, duplicateOrder, records } = useSelector((state) => state.salesOrder)
  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.ORDER, action)
  }
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    cd_cond_pgto,
    ds_cond_pgto,
    mescotas,
    cd_seto_atvd,
    fl_mto_prep,
    cd_clie,
    nm_clie,
    cd_empr,
    cd_orgn_vend,
    cd_canl_dstb,
    nr_pedi_rasc,
    cd_regi_vend,
    ds_regi_vend,
    cd_escr_vend,
    nr_cpnj_cpf_entr: nr_cnpj_cpf,
    tp_orig,
  } = draftBody

  const { orderNumber } = useParams()

  const isRestricted = useMemo(
    () => records.length && records.every((record) => record.cd_stat_pedi_rasc_item === 90),
    [records],
  )

  useEffect(() => {
    dispatch(clearMaterialSelected())
  }, [])

  function paymentLabel() {
    if (!!cd_cond_pgto && !!ds_cond_pgto) {
      return `${cd_cond_pgto} - ${ds_cond_pgto}`
    }
    return undefined
  }

  const handleDeleteDraftOrder = async () => {
    await DraftOV.deleteDraftOV({
      cd_usua_alte: getUser(),
      dt_alte_antr: draftBody.dt_alte,
      nr_pedi_rasc: draftBody.nr_pedi_rasc,
    }).then(() => toast.success('O pedido foi excluído com sucesso.'))
  }

  const handlePostOnTopSection = async (array: ClientsListInterface[]) => {
    const body = getPostOnBody(array, draftBody, orderNumber)

    const post = await DraftOV.duplicateDraftOV(body)
    if (post) {
      navigate(URL_CONST.ORDERS)
    }
    dispatch(setCheckedRecords([]))
  }

  const handleDuplicateOrder = async () => {
    await DraftOV.duplicateDraftOV(duplicateOrder).then((response) => {
      if (response) {
        dispatch(setConfirmedNavigation(true))
        navigate(`${URL_CONST.ORDERS}/${response[0]?.NumeroPedido?.trim()}`)
        toast.success(
          'Pedido duplicado com sucesso. Isso gerou um novo Pedido Rascunho com as configurações selecionadas.',
        )
      } else {
        toast.error('Erro ao duplicar pedido.')
      }
    })
    setDuplicateOrder({ ...duplicateOrder, Clientes: [] })
  }

  return (
    <TopSection
      defaultClient={{
        cd_canl_dstb,
        cd_clie,
        cd_empr,
        cd_escr_vend,
        cd_orgn_vend,
        cd_regi_vend,
        cd_seto_atvd,
        ds_regi_vend,
        nm_clie,
        nr_cnpj_cpf,
      }}
      defaultPayment={paymentLabel()}
      disableClientConfig={!!records.length || shouldDisable(ACTIONS_SCREENS.EDIT)}
      disableFields
      disableOrderConfig={step !== 0 || isRestricted || shouldDisable(ACTIONS_SCREENS.EDIT)}
      disableToolbar={shouldDisable(ACTIONS_SCREENS.EDIT)}
      hasOvs={hasOVs}
      isButtonRescueData={isButtonRescueData}
      isEDI={tp_orig === OriginOrder.EDI}
      materialTypeMTO={fl_mto_prep === TypesValidations.YES}
      onClickDelete={handleDeleteDraftOrder}
      onClickDuplicate={handleDuplicateOrder}
      onClickPostOn={handlePostOnTopSection}
      orderNumber={nr_pedi_rasc}
      quotasMonth={mescotas}
      step={step}
    />
  )
}

export default ConfigOrder
